/**
 * Canonical export column headers for Echo audit-log exports.
 *
 * This list is the SINGLE SOURCE OF TRUTH for what a user can pick when
 * running an export. Order matters — writers render columns in this
 * order regardless of the sequence a caller lists them in on the
 * request body, so every export file across every org looks
 * consistent.
 *
 * The frontend column picker renders from this list. The backend
 * writers (apps/backend/src/echo/infrastructure/export-writers/
 * columns.ts) attach extractors to each header — extractors are
 * backend-only because they depend on derived-field helpers not
 * appropriate in the frontend bundle.
 */
export const ECHO_EXPORT_COLUMN_HEADERS = [
  "timestamp",
  "event_type",
  "event_category",
  "action",
  "status",
  "severity",
  "actor_name",
  "actor_email",
  "actor_id",
  "actor_type",
  "resource_type",
  "resource_name",
  "resource_id",
  "ip_address",
  "location",
  "user_agent",
  "duration_ms",
  "request_id",
  "session_id",
  "description",
  "error_code",
  "error_message",
  "compliance_tags",
  "sensitive_data",
  "redacted",
  "event_id"
] as const;

/** Literal union of every canonical column header. */
export type EchoExportColumnHeader = (typeof ECHO_EXPORT_COLUMN_HEADERS)[number];

/**
 * Human-readable label for each column. Used by the export dialog's
 * column picker. Falling back to the raw header is safe when a column
 * is added here but not yet in this label map.
 */
export const ECHO_EXPORT_COLUMN_LABELS: Record<EchoExportColumnHeader, string> = {
  timestamp: "Timestamp",
  event_type: "Event type",
  event_category: "Event category",
  action: "Action",
  status: "Status",
  severity: "Severity",
  actor_name: "Actor name",
  actor_email: "Actor email",
  actor_id: "Actor ID",
  actor_type: "Actor type",
  resource_type: "Resource type",
  resource_name: "Resource name",
  resource_id: "Resource ID",
  ip_address: "IP address",
  location: "Location",
  user_agent: "User agent",
  duration_ms: "Duration (ms)",
  request_id: "Request ID",
  session_id: "Session ID",
  description: "Description",
  error_code: "Error code",
  error_message: "Error message",
  compliance_tags: "Compliance tags",
  sensitive_data: "Sensitive data",
  redacted: "Redacted",
  event_id: "Event ID"
};
